import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainHeaderWithButton } from '@eposnow/ui-core';
import { Box, Button, CircularProgress, Pagination, Typography, useTheme } from '@mui/material';
import { ReactComponent as MagnifyingGlass } from '../../../img/icons/warning-magnifying-glass.svg';
import { ReactComponent as WhiteMagnifyingGlass } from '../../../img/icons/warning-magnifying-glass-white.svg';
import { UIContext } from '../../../context/UIContext';
import { Category, ProductCreateEdit, ProductSuggestion } from '../../../types';
import { deepClone } from '../../../helpers/helpers';
import MobileRowStructure from './mobileTableHelpers/MobileRowStructure';
import {
  MobileRowExpandableContent,
  MobileRowMainContent,
} from './mobileTableHelpers/MobileRowContent';

const SidekickMobileProductsTable = ({
  tableData,
  categories,
  isLoading,
  isAddingProducts,
  addProducts,
  refetchProducts,
  emptyStateTitle,
  emptyStateSubtitle,
}: {
  tableData: ProductSuggestion[];
  categories: Category[];
  isLoading: boolean;
  isAddingProducts: boolean;
  addProducts: (newProducts: ProductCreateEdit[]) => void;
  refetchProducts: () => void;
  emptyStateTitle: string;
  emptyStateSubtitle: string;
}) => {
  const [rows, setRows] = useState(tableData);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
    indexFirstDataRow: 0,
    indexLastDataRow: 10,
  });
  const { t } = useTranslation();
  const theme = useTheme();
  const [dataForPage, setDataForPage] = useState([]);
  const { colorMode } = useContext(UIContext);

  const handleChangePage = (_event, newPage) => {
    const indexLastDataRow = paginationModel.pageSize * (newPage - 1) + paginationModel.pageSize;
    const indexFirstDataRow = indexLastDataRow - paginationModel.pageSize;
    setPaginationModel({
      ...paginationModel,
      page: newPage - 1,
      indexFirstDataRow,
      indexLastDataRow,
    });
  };

  useEffect(() => {
    if (rows) {
      setDataForPage(
        rows.slice(paginationModel.indexFirstDataRow, paginationModel.indexLastDataRow)
      );
    }
  }, [rows, paginationModel]);

  const parseAndAddProducts = (id?: string) => {
    if (dataForPage?.length === 0) return;
    if (id != null) {
      const selectedProduct: ProductSuggestion = deepClone(
        dataForPage.find((row) => row.placeholderId === id)
      );
      const newProduct: ProductCreateEdit = {
        name: selectedProduct.name.replace(/[',<>#]/g, ''),
        salePrice: selectedProduct.price,
        categoryId: selectedProduct.categoryId === -1 ? null : selectedProduct.categoryId,
        description: selectedProduct.description.replace(/["'\\#]/g, ''),
        costPrice: 0,
      };
      addProducts([newProduct]);
    } else {
      addProducts(
        rows.map((row) => ({
          name: row.name.replace(/[',<>#]/g, ''),
          salePrice: row.price,
          description: row.description.replace(/["'\\#]/g, ''),
          categoryId: row.categoryId === -1 ? null : row.categoryId,
          costPrice: 0,
        }))
      );
    }
  };

  const handleAddClick = (id: string) => {
    parseAndAddProducts(id);
  };

  const handleDuplicateClick = (id: string) => {
    const newRow = deepClone(dataForPage.find((row) => row.placeholderId === id));
    const newRows = deepClone(dataForPage);
    newRows.splice(id, 0, newRow);
    setRows(newRows.map((row, index) => ({ ...row, placeholderId: index?.toString() })));
  };

  const handleDeleteClick = (id: string) => {
    const newRows = dataForPage.filter((row) => row.placeholderId !== id);
    setRows(newRows.map((row, index) => ({ ...row, placeholderId: index?.toString() })));
  };

  return (
    <>
      <Box
        sx={{
          marginBottom: { xs: 2, sm: 3 },
          marginLeft: 0,
          paddingX: { xs: 2, sm: 0 },
          marginX: '-1rem',
        }}
      >
        <MainHeaderWithButton
          title={t(`steps.products.title`)}
          isMobile
          theme={theme}
          styles={{ paddingBottom: 0 }}
        >
          {tableData?.length > 0 && (
            <Button
              color="primary"
              data-qa-id="button-add-to-catalogue"
              variant="contained"
              disableElevation
              onClick={() => parseAndAddProducts()}
            >
              {isAddingProducts && (
                <>
                  <CircularProgress
                    aria-label={t('screens.products.sidekick.adding')}
                    aria-live="polite"
                    aria-busy={isAddingProducts}
                    color="inherit"
                    size="20px"
                    sx={{ marginRight: 1 }}
                  />
                  {t('screens.products.sidekick.adding')}
                </>
              )}
              {!isAddingProducts && t('screens.products.sidekick.addAll')}
            </Button>
          )}
        </MainHeaderWithButton>

        <Typography variant="body1" sx={{ paddingTop: '0.5rem' }}>
          {t(`steps.products.description`)}
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100vw',
          marginTop: '2rem',
          marginLeft: '-1rem',
          marginBottom: '3rem',
          borderTop: `1px ${
            colorMode === 'light' ? theme?.palette?.grey?.['12p'] : theme?.palette?.grey?.['23p']
          } solid`,
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              margin: '96px',
            }}
          >
            <CircularProgress
              aria-label={t('components.loading.loadingData')}
              aria-live="polite"
              aria-busy={isLoading}
              data-testid="loading-icon"
              sx={{ color: 'text.secondary' }}
            />
          </Box>
        )}
        {!isLoading && (
          <>
            {tableData.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderBottom: `1px ${
                    colorMode === 'light'
                      ? theme?.palette?.grey?.['12p']
                      : theme?.palette?.grey?.['23p']
                  } solid`,
                }}
              >
                {colorMode === 'light' ? <MagnifyingGlass /> : <WhiteMagnifyingGlass />}
                <Typography variant="h2">{t(emptyStateTitle)}</Typography>
                <Typography variant="body1">{t(emptyStateSubtitle)}</Typography>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    sx={{
                      height: '48px',
                    }}
                    onClick={refetchProducts}
                  >
                    {t('actions.retry')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  backgroundColor: 'background.paper',
                }}
              >
                {dataForPage?.map((rowData) => (
                  <Box key={rowData.placeholderId ?? rowData.placeholderId}>
                    <MobileRowStructure
                      mainContent={<MobileRowMainContent data={rowData} />}
                      expandableContent={
                        <MobileRowExpandableContent
                          data={rowData}
                          isLoading={isLoading}
                          categories={categories}
                          handleDeleteClick={handleDeleteClick}
                          handleAddClick={handleAddClick}
                          handleDuplicateClick={handleDuplicateClick}
                        />
                      }
                    />
                  </Box>
                ))}
              </Box>
            )}
            {dataForPage.length > 0 && tableData.length > paginationModel.pageSize && (
              <Pagination
                count={Math.ceil(tableData.length / paginationModel.pageSize)}
                page={paginationModel.page + 1}
                onChange={handleChangePage}
                color="primary"
                sx={{
                  '.MuiPagination-ul': { justifyContent: 'center', marginY: 2 },
                }}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default SidekickMobileProductsTable;
